"use client"

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { useAuthStore } from '../stores/auth-store';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';

interface LoginPageProps {
  onNavigateToRegister: () => void;
}

const reduced = () =>
  typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

// Deterministic night sky — no Math.random, so the field is stable across
// renders/SSR. Only visible in the dark (Odisseia) theme.
const LOGIN_STARS = Array.from({ length: 24 }, (_, i) => ({
  left: (i * 41) % 100,
  top: ((i * 29) % 92) + 3,
  size: 1 + ((i * 5) % 3),
  delay: (i % 13) * 0.11,
  dur: 2.6 + ((i * 7) % 5) * 0.35,
}));

export function LoginPage({ onNavigateToRegister }: LoginPageProps) {
  const { t } = useTranslation();
  const { login, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [motionOk] = useState(() => !reduced());
  const [shootingStar, setShootingStar] = useState(false);
  // Local submit state — this route is standalone (not wrapped by
  // ProtectedRoute, which is the only place that resolves the store's
  // isLoading via checkAuth()), so the button must not depend on that
  // global flag or it would stay stuck disabled.
  const [submitting, setSubmitting] = useState(false);

  // A rare shooting star, once, shortly after landing — never loops, never distracts.
  useEffect(() => {
    if (!motionOk) return;
    const id = window.setTimeout(() => setShootingStar(true), 3200);
    return () => window.clearTimeout(id);
  }, [motionOk]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      await login(email, password);
    } finally {
      setSubmitting(false);
    }
  };

  const fieldMotion = (delay: number) =>
    motionOk
      ? {
          initial: { opacity: 0, y: 10 },
          animate: { opacity: 1, y: 0 },
          transition: { delay, duration: 0.5, ease: EASE_OUT },
        }
      : { initial: false as const };

  return (
    <div className="login-wrap relative flex min-h-screen w-full items-center justify-center overflow-y-auto bg-background p-5">
      {/* night sky — cascades in once, then breathes softly; dark theme only */}
      <div
        className="pointer-events-none fixed inset-0 z-0 overflow-hidden opacity-0 dark:opacity-100"
        aria-hidden="true"
      >
        {LOGIN_STARS.map((st, i) => (
          <motion.span
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${st.left}%`,
              top: `${st.top}%`,
              width: st.size,
              height: st.size,
              background: '#F6E9C8',
              boxShadow: '0 0 6px 1px rgba(246,233,200,0.5)',
            }}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={
              motionOk
                ? { opacity: [0, 0.85, 0.35, 0.65, 0.35], scale: 1 }
                : { opacity: 0.32, scale: 1 }
            }
            transition={
              motionOk
                ? {
                    delay: 0.2 + st.delay,
                    duration: st.dur,
                    times: [0, 0.18, 0.42, 0.7, 1],
                    repeat: Infinity,
                    repeatDelay: st.dur * 0.55,
                    ease: 'easeInOut',
                  }
                : { duration: 0 }
            }
          />
        ))}

        {motionOk && shootingStar && (
          <motion.span
            className="absolute"
            style={{ left: '6%', top: '14%' }}
            initial={{ x: 0, y: 0, opacity: 0 }}
            animate={{ x: '46vw', y: '14vh', opacity: [0, 1, 1, 0] }}
            transition={{ duration: 1.15, ease: [0.3, 0.55, 0.4, 1] }}
            onAnimationComplete={() => setShootingStar(false)}
          >
            <span
              className="block rounded-full"
              style={{
                width: 2,
                height: 2,
                background: '#F6E9C8',
                boxShadow: '0 0 8px 2px rgba(246,233,200,0.9)',
              }}
            />
            <span
              className="absolute right-[1px] top-1/2 h-px w-20"
              style={{
                transform: 'translateY(-50%) rotate(11deg)',
                transformOrigin: 'right center',
                background: 'linear-gradient(270deg, rgba(246,233,200,0.85), transparent)',
              }}
            />
          </motion.span>
        )}
      </div>

      <div className="relative z-10 w-[min(370px,94vw)] text-center">
        <motion.div
          initial={motionOk ? { opacity: 0, y: -6 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: motionOk ? 0.6 : 0, ease: EASE_OUT }}
          className="relative mx-auto mb-[18px]"
          style={{ width: 74, height: 74 }}
        >
          {/* the moon breathes — a soft halo pulsing behind it */}
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -inset-6 rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(246,233,200,0.35), transparent 70%)' }}
            animate={
              motionOk
                ? { opacity: [0.35, 0.75, 0.35], scale: [1, 1.12, 1] }
                : { opacity: 0.45, scale: 1 }
            }
            transition={
              motionOk
                ? { duration: 4.2, repeat: Infinity, ease: 'easeInOut' }
                : { duration: 0 }
            }
          />
          <div className="login-moon" aria-hidden="true" style={{ margin: 0, width: '100%', height: '100%' }} />
        </motion.div>

        <motion.h1
          initial={motionOk ? { opacity: 0, y: 8 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: motionOk ? 0.14 : 0, duration: motionOk ? 0.5 : 0, ease: EASE_OUT }}
          className="font-serif font-medium text-[26px] leading-tight text-foreground"
        >
          reformu<em className="italic" style={{ color: 'var(--ember-hot)' }}>.lat</em>
        </motion.h1>
        <motion.p
          initial={motionOk ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ delay: motionOk ? 0.22 : 0, duration: motionOk ? 0.5 : 0 }}
          className="mb-5 mt-1 text-[12px] text-muted-foreground"
        >
          {t('auth.loginSubtitle', 'Your professional book writing platform')}
        </motion.p>
        <h2 className="sr-only">{t('auth.signIn', 'Sign in')}</h2>
        <p className="sr-only">
          {t('auth.signInDescription', 'Enter your credentials to access your account')}
        </p>

        <form onSubmit={handleSubmit} className="text-left">
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, height: 0, transition: { duration: 0.2 } }}
                transition={{ duration: 0.3, ease: EASE_OUT }}
                className="mb-3 overflow-hidden"
              >
                <Alert variant="destructive" className="text-left">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div className="mb-2.5" {...fieldMotion(0.28)}>
            <Label htmlFor="email" className="sr-only">
              {t('auth.email', 'Email')}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              disabled={submitting}
              className="h-auto rounded-[11px] bg-card px-4 py-3 text-sm ring-1 ring-inset ring-border focus-visible:ring-2 focus-visible:ring-ember/50"
            />
          </motion.div>

          <motion.div className="mb-1" {...fieldMotion(0.34)}>
            <Label htmlFor="password" className="sr-only">
              {t('auth.password', 'Password')}
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={submitting}
              className="h-auto rounded-[11px] bg-card px-4 py-3 text-sm ring-1 ring-inset ring-border focus-visible:ring-2 focus-visible:ring-ember/50"
            />
          </motion.div>

          <motion.div className="mb-4 flex justify-end" {...fieldMotion(0.4)}>
            <Link
              to="/forgot-password"
              className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-muted-foreground hover:text-foreground"
            >
              {t('auth.forgotPassword', 'Esqueceu a senha?')}
            </Link>
          </motion.div>

          <motion.div className="relative" {...fieldMotion(0.46)}>
            {/* a quiet ember glow breathing behind the CTA */}
            {motionOk && (
              <motion.span
                aria-hidden
                className="pointer-events-none absolute -inset-2 rounded-[13px]"
                style={{ background: 'radial-gradient(circle, rgba(246,233,200,0.4), transparent 72%)', filter: 'blur(6px)' }}
                animate={{ opacity: submitting ? 0.18 : [0.22, 0.5, 0.22] }}
                transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
              />
            )}
            <button
              type="submit"
              className="btn-ember relative w-full"
              disabled={submitting || !email || !password}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('auth.signingIn', 'Signing in...')}
                </>
              ) : (
                <>
                  <span aria-hidden="true">✦</span> {t('auth.signIn', 'Sign in')}
                </>
              )}
            </button>
          </motion.div>
        </form>

        <motion.p
          className="login-alt mt-3.5 font-mono text-[8.5px] uppercase tracking-[0.16em] text-muted-foreground"
          {...fieldMotion(0.52)}
        >
          {t('auth.noAccount', "Don't have an account?")}{' '}
          <button
            type="button"
            onClick={onNavigateToRegister}
            className="cursor-pointer font-semibold text-ember hover:text-ember-hot"
          >
            {t('auth.signUp', 'Sign up')}
          </button>
        </motion.p>

        <motion.p
          className="mx-auto mt-6 max-w-[300px] text-center text-[10px] text-muted-foreground"
          {...fieldMotion(0.58)}
        >
          {t('auth.termsNotice', 'By signing in, you agree to our Terms of Service and Privacy Policy.')}
        </motion.p>
      </div>
    </div>
  );
}
